// Spec §4.3: Extract debounce click logic from NormalButton component
import dayjs from 'dayjs';
import { useRef } from 'react';
import type { NormalButtonProps } from './types';

/**
 * Empty default callback standing in when the button receives no onClick prop. Takes no arguments and performs no work.
 *
 * @returns Nothing.
 */
const NOOP = () => {};

/**
 * Debounces button presses so a rapid double click triggers only one action. Defaults a missing onClick to a no-op and exposes an onClick that drops events arriving within 300ms of the timestamp kept in a ref, otherwise recording the new time and calling the parent handler with the event.
 *
 * @param props - The button props, carrying the parent onClick callback and the isDebounceClick flag.
 * @returns The debounced click handler.
 */
export const useNormalButton = (props: NormalButtonProps) => {
  const { isDebounceClick = true, onClick: onClickProp = NOOP } = props;
  const debounceClickBottomButton = useRef(0);

  /**
   * Invokes the button's parent click handler while suppressing rapid repeat presses. Returns early when debouncing is enabled and less than 300ms elapsed since the ref-stored timestamp, otherwise records the current time and calls onClickProp with the event.
   *
   * @param e - The click event.
   * @returns Nothing.
   */
  const onClick = (e: any) => {
    if (isDebounceClick && dayjs().valueOf() - debounceClickBottomButton.current < 300) {
      return;
    }

    debounceClickBottomButton.current = dayjs().valueOf();
    onClickProp(e);
  };

  return { onClick };
};
